/**
 * Builds the results spreadsheet.
 *
 * One sheet per kind of record rather than one wide sheet, so each row stays a
 * single fact with the address it belongs to. That keeps the file usable as
 * evidence: anyone can filter a sheet by address and see exactly what was found,
 * and the Method sheet states what was searched and what could not be.
 */

import * as XLSX from "xlsx"

/**
 * Rough column widths so the file opens readable rather than needing every column
 * dragged out.
 */
const widthFor = (header) => {
	const key = String(header).toLowerCase()

	if (key.includes("email") || key.includes("address")) return 34
	if (key.includes("subject") || key.includes("campaign") || key.includes("notes") || key.includes("url")) return 46
	if (key.includes("date") || key.includes("time") || key.includes("created") || key.includes("changed") || key.includes("added")) return 22
	if (key.includes("id")) return 24

	return 16
}

const appendSheet = (workbook, name, rows) => {
	// Excel refuses sheet names over 31 characters
	const sheetName = name.slice(0, 31)

	if (rows.length === 0) {
		const empty = XLSX.utils.aoa_to_sheet([["Nothing found"]])
		XLSX.utils.book_append_sheet(workbook, empty, sheetName)
		return
	}

	const sheet = XLSX.utils.json_to_sheet(rows)
	const headers = Object.keys(rows[0])

	sheet["!cols"] = headers.map((header) => ({ wch: widthFor(header) }))
	sheet["!freeze"] = { xSplit: 0, ySplit: 1 }

	XLSX.utils.book_append_sheet(workbook, sheet, sheetName)
}

/**
 * The subscription state of an address in one phrase, for the summary row.
 */
export const describeSubscriptionState = (memberships) => {
	if (memberships.length === 0) return "Not on any mailing list"

	const statuses = [...new Set(memberships.map((membership) => membership.status).filter(Boolean))]

	if (statuses.includes("subscribed")) return "Currently subscribed"
	if (statuses.every((status) => status === "unsubscribed")) return "Unsubscribed"
	if (statuses.every((status) => status === "cleaned")) return "Cleaned (bounced)"

	return `Previously on a list (${statuses.join(", ")})`
}

/**
 * The subscription line for one address, as a reader should see it.
 *
 * An address whose lookup failed is never described as "not on any mailing list":
 * absence of a result is not a finding, and a report that blurs the two is worse
 * than no report.
 */
export const subscriptionSummary = (result, { short = false } = {}) => {
	if (result.status === "ERROR") return short ? "Not established" : "Not established - the lookup did not complete"
	if (result.mailchimpSkipped) return short ? "Not checked" : "Not checked - no Mailchimp key was supplied"
	if (result.mailchimp?.error) return short ? "Not established" : "Not established - Mailchimp could not be searched"

	return describeSubscriptionState(result.mailchimp?.memberships || [])
}

/**
 * Earliest signup date across every audience, which is the answer to "when were
 * they added to a mailing list".
 */
export const earliestSignup = (memberships) => {
	const dates = memberships.map((membership) => membership.signedUp || membership.optedIn).filter(Boolean).sort()

	return dates[0] || ""
}

export const buildResultsWorkbook = (results, runInfo) => {
	const workbook = XLSX.utils.book_new()

	// Summary - one row per address. Counts on a row whose lookup failed are left
	// blank rather than written as 0, so a failure can never be read as a finding
	const count = (result, value) => (result.status === "ERROR" && !value ? "" : value)

	appendSheet(
		workbook,
		"Summary",
		results.map((result) => ({
			"Email address": result.email,
			"Source row": result.source,
			Outcome: result.status === "ERROR" ? `Error: ${result.error}` : "Checked",
			"Subscription status": subscriptionSummary(result),
			"Mailing lists": (result.mailchimp?.memberships || []).map((membership) => membership.audienceName).join(", "),
			"First added to a list": result.mailchimpSkipped ? "" : earliestSignup(result.mailchimp?.memberships || []),
			"Marketing events recorded": result.mailchimpSkipped ? "" : count(result, (result.mailchimp?.activity || []).length),
			"Booking system accounts": count(result, result.clients.length),
			"Client IDs": result.clients.map((client) => client.id).join(", "),
			Name: result.clients.map((client) => [client.firstName, client.lastName].filter(Boolean).join(" ")).filter(Boolean).join(" / "),
			Phone: result.clients.map((client) => client.phone).filter(Boolean).join(" / "),
			"Account created": result.clients.map((client) => client.created).filter(Boolean).sort()[0] || "",
			Enquiries: count(result, result.enquiries.length),
			Bookings: count(result, result.bookings.length),
			"Live bookings": count(result, result.bookings.filter((booking) => booking.state === "Live").length),
			Quotes: count(result, result.quotes.length),
			"Lifetime value (GBP)": count(result, result.totals.lifetimeValue),
			"Paid by client (GBP)": count(result, result.totals.paidByClient),
			"Payments recorded (GBP)": count(result, result.totals.paymentsRecorded),
			"Gift vouchers": count(result, result.giftVouchers.length),
			"Emails on file": result.correspondence.accessible ? count(result, result.correspondence.messages.length) : "Not accessible",
			"Duplicate rows in source": result.duplicateOf.join(", "),
		}))
	)

	// Mailing list memberships
	const memberships = []

	results.forEach((result) => {
		(result.mailchimp?.memberships || []).forEach((membership) => {
			memberships.push({
				"Email address": result.email,
				Audience: membership.audienceName,
				"Audience ID": membership.listId,
				Status: membership.status,
				Archived: membership.archived ? "Yes" : "No",
				"Signed up": membership.signedUp,
				"Opted in": membership.optedIn,
				"Last changed": membership.lastChanged,
				"Unsubscribe reason": membership.unsubscribeReason,
				Source: membership.source,
				"Signup IP": membership.signupIP,
				"Opt-in IP": membership.optInIP,
				"Name on record": [membership.firstName, membership.lastName].filter(Boolean).join(" "),
				"Phone on record": membership.phone,
				Tags: membership.tags.join(", "),
				Rating: membership.rating,
				"Average open rate": membership.openRate,
				"Average click rate": membership.clickRate,
				VIP: membership.vip ? "Yes" : "No",
				"Contact ID": membership.contactID,
			})
		})
	})

	appendSheet(workbook, "Mailing lists", memberships)

	// Tag history - when each tag was applied
	const tags = []

	results.forEach((result) => {
		(result.mailchimp?.memberships || []).forEach((membership) => {
			(membership.tagDetail || []).forEach((tag) => {
				tags.push({
					"Email address": result.email,
					Audience: membership.audienceName,
					Tag: tag.name,
					"Date added": tag.added,
				})
			})
		})
	})

	appendSheet(workbook, "Tag history", tags)

	// Marketing activity
	const activity = []

	results.forEach((result) => {
		(result.mailchimp?.activity || []).forEach((event) => {
			activity.push({
				"Email address": result.email,
				Date: event.timestamp,
				Action: event.action,
				Audience: event.audienceName,
				Campaign: event.campaignTitle,
				"Campaign subject": event.campaignSubject,
				"Campaign sent": event.campaignSendTime,
				"Campaign ID": event.campaignID,
				"Link clicked": event.url,
				Type: event.type,
				IP: event.ip,
			})
		})
	})

	appendSheet(workbook, "Marketing activity", activity)

	// Client accounts
	const clients = []

	results.forEach((result) => {
		result.clients.forEach((client) => {
			clients.push({
				"Email address": result.email,
				"Client ID": client.id,
				"Email on account": client.email,
				"First name": client.firstName,
				"Last name": client.lastName,
				Phone: client.phone,
				Address: client.address,
				Created: client.created,
				"Portal login": client.hasPortalLogin ? "Yes" : "No",
			})
		})
	})

	appendSheet(workbook, "Client accounts", clients)

	// Enquiries
	const enquiries = []

	results.forEach((result) => {
		result.enquiries.forEach((enquiry) => {
			enquiries.push({
				"Email address": result.email,
				"Enquiry ID": enquiry.id,
				"Client ID": enquiry.clientID,
				State: enquiry.state,
				Raised: enquiry.created,
				"Travel date": enquiry.travelDate,
				Destination: enquiry.location,
				"Group size": enquiry.groupSize,
				Nights: enquiry.nights,
				Rounds: enquiry.rounds,
				"Quoted (GBP)": enquiry.quoted,
				Status: enquiry.status,
				"Inbound channel": enquiry.inboundChannel,
				Site: enquiry.site,
				Agent: enquiry.agent,
				"UTM source": enquiry.utmSource,
				"UTM campaign": enquiry.utmCampaign,
				"UTM medium": enquiry.utmMedium,
				Deleted: enquiry.deletedAt || "",
				"Deleted by": enquiry.deletedBy || "",
				Notes: enquiry.notes,
			})
		})
	})

	appendSheet(workbook, "Enquiries", enquiries)

	// Bookings
	const bookings = []

	results.forEach((result) => {
		result.bookings.forEach((booking) => {
			bookings.push({
				"Email address": result.email,
				"Booking ID": booking.id,
				Reference: booking.reference,
				"Client ID": booking.clientID,
				State: booking.state,
				Confirmed: booking.confirmed ? "Yes" : "No",
				Created: booking.created,
				Booked: booking.booked,
				"Travel date": booking.travelDate,
				Destination: booking.location,
				Golfers: booking.golfers,
				"Non-golfers": booking.nonGolfers,
				"Holiday cost (GBP)": booking.customerPrice,
				"Cost read from": booking.customerPriceSource,
				"Paid by client (GBP)": booking.paidByClient,
				"Payments recorded (GBP)": booking.paymentsRecorded,
				"Payment count": booking.paymentCount,
				Agent: booking.agent,
				Event: booking.eventTag,
			})
		})
	})

	appendSheet(workbook, "Bookings", bookings)

	// Quotes
	const quotes = []

	results.forEach((result) => {
		result.quotes.forEach((quote) => {
			quotes.push({
				"Email address": result.email,
				"Quote ID": quote.id,
				Kind: quote.kind,
				"Client ID": quote.clientID,
				Created: quote.created,
				Status: quote.status,
				Destination: quote.location,
				"Value (GBP)": quote.value,
				"First viewed": quote.viewedAt || "",
			})
		})
	})

	appendSheet(workbook, "Quotes", quotes)

	// Direct (online) bookings
	const directBookings = []

	results.forEach((result) => {
		result.directBookings.forEach((booking) => {
			directBookings.push({
				"Email address": result.email,
				"Direct booking ID": booking.id,
				"Client ID": booking.clientID,
				Created: booking.created,
				"Travel date": booking.travelDate,
				Destination: booking.location,
				"Total (GBP)": booking.total,
				"Paid by client (GBP)": booking.paidByClient,
				"Paid status": booking.paidStatus,
			})
		})
	})

	appendSheet(workbook, "Direct bookings", directBookings)

	// Gift vouchers
	const vouchers = []

	results.forEach((result) => {
		result.giftVouchers.forEach((voucher) => {
			vouchers.push({
				"Email address": result.email,
				"Voucher ID": voucher.id,
				Created: voucher.created,
				"Value (GBP)": voucher.value,
				Type: voucher.type,
				Status: voucher.status,
				"Bought by": [voucher.firstName, voucher.lastName].filter(Boolean).join(" "),
			})
		})
	})

	appendSheet(workbook, "Gift vouchers", vouchers)

	// Correspondence
	const correspondence = []

	results.forEach((result) => {
		result.correspondence.messages.forEach((message) => {
			correspondence.push({
				"Email address": result.email,
				"Message ID": message.id,
				Date: message.occurredAt,
				Direction: message.direction,
				Type: message.type,
				From: message.from,
				To: message.to,
				Subject: message.subject,
				Classification: message.classification,
				"Has unsubscribe link": message.hasUnsubscribeLink ? "Yes" : "No",
			})
		})
	})

	appendSheet(workbook, "Correspondence", correspondence)

	// Fuzzy Mailchimp matches - never treated as the subject's own record
	const related = []

	results.forEach((result) => {
		(result.mailchimp?.related || []).forEach((contact) => {
			related.push({
				"Searched address": result.email,
				"Related contact": contact.email,
				Status: contact.status,
				"Audience ID": contact.listId,
				Note: "Fuzzy match returned by Mailchimp search, not the same address",
			})
		})
	})

	appendSheet(workbook, "Possible related contacts", related)

	// Anything that failed
	appendSheet(
		workbook,
		"Errors",
		results
			.filter((result) => result.status === "ERROR" || result.mailchimp?.error)
			.map((result) => ({
				"Email address": result.email,
				"Lookup error": result.error || "",
				"Mailchimp error": result.mailchimp?.error || "",
			}))
	)

	// How the run was performed
	appendSheet(
		workbook,
		"Method",
		runInfo.method.map((entry) => ({ Item: entry.label, Detail: entry.value }))
	)

	const array = XLSX.write(workbook, { bookType: "xlsx", type: "array" })

	return new Blob([array], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
}
